import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useIsAuthenticated } from '@azure/msal-react';

import { AuthenticatedLayout } from '@/shared/components/layouts/AuthenticatedLayout';
import { LoadingSpinner } from '@/shared/components/ui/LoadingSpinner';
import { ProtectedRoute } from '@/shared/components/auth/ProtectedRoute';
import { ErrorBoundary } from '@/shared/components/ErrorBoundary';
import { UserRole } from '@/types/auth';
import { isMockAuthEnabled, isInTeams, getStoredTeamsSsoToken } from '@/lib/auth';

// Lazy load pages for code splitting
const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage'));
const DashboardPage = lazy(() => import('@/features/dashboard/pages/DashboardPage'));
const UseCaseListPage = lazy(() => import('@/features/use-cases/pages/UseCaseListPage'));
const UseCaseDetailPage = lazy(() => import('@/features/use-cases/pages/UseCaseDetailPage'));
const UseCaseFormPage = lazy(() => import('@/features/use-cases/pages/UseCaseFormPage'));
const AIToolsListPage = lazy(() => import('@/features/ai-tools/pages/AIToolsListPage'));
const AIToolDetailPage = lazy(() => import('@/features/ai-tools/pages/AIToolDetailPage'));
const ToolRequestFormPage = lazy(() => import('@/features/ai-tools/pages/ToolRequestFormPage'));
const ApprovalsPage = lazy(() => import('@/features/approvals/pages/ApprovalsPage'));
const ReportsPage = lazy(() => import('@/features/reports/pages/ReportsPage'));
const AdminPage = lazy(() => import('@/features/admin/pages/AdminPage'));
const SearchPage = lazy(() => import('@/features/search/pages/SearchPage'));
const NotFoundPage = lazy(() => import('@/shared/components/pages/NotFoundPage'));
const UnauthorizedPage = lazy(() => import('@/shared/components/pages/UnauthorizedPage'));
const TeamsConfig = lazy(() => import('@/features/teams/TeamsConfig'));

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <LoadingSpinner size="lg" />
    </div>
  );
}

function App() {
  const msalIsAuthenticated = useIsAuthenticated();
  // Check for Teams SSO authentication (token acquired during initialization)
  const teamsIsAuthenticated = isInTeams() && !!getStoredTeamsSsoToken();
  // In mock mode or Teams SSO, always consider user authenticated
  const isAuthenticated = isMockAuthEnabled || msalIsAuthenticated || teamsIsAuthenticated;

  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />}
        />

        {/* Teams tab configuration page */}
        <Route path="/teams-config" element={<TeamsConfig />} />

        {/* Protected routes */}
        <Route element={<AuthenticatedLayout />}>
          <Route path="/" element={<DashboardPage />} />

          {/* Use Cases */}
          <Route path="/use-cases" element={<UseCaseListPage />} />
          <Route
            path="/use-cases/new"
            element={
              <ProtectedRoute
                allowedRoles={[
                  UserRole.AI_CHAMPION,
                  UserRole.AI_COUNCIL,
                  UserRole.IT_TEAM,
                  UserRole.EXECUTIVE_SPONSOR,
                  UserRole.ADMIN,
                ]}
              >
                <UseCaseFormPage />
              </ProtectedRoute>
            }
          />
          <Route path="/use-cases/:id" element={<UseCaseDetailPage />} />
          <Route path="/use-cases/:id/edit" element={<UseCaseFormPage />} />

          {/* AI Tools */}
          <Route path="/ai-tools" element={<AIToolsListPage />} />
          <Route
            path="/ai-tools/request"
            element={
              <ProtectedRoute
                allowedRoles={[
                  UserRole.AI_CHAMPION,
                  UserRole.AI_COUNCIL,
                  UserRole.IT_TEAM,
                  UserRole.EXECUTIVE_SPONSOR,
                  UserRole.ADMIN,
                ]}
              >
                <ToolRequestFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ai-tools/:id/edit"
            element={
              <ProtectedRoute
                allowedRoles={[
                  UserRole.AI_CHAMPION,
                  UserRole.AI_COUNCIL,
                  UserRole.IT_TEAM,
                  UserRole.EXECUTIVE_SPONSOR,
                  UserRole.ADMIN,
                ]}
              >
                <ToolRequestFormPage />
              </ProtectedRoute>
            }
          />
          <Route path="/ai-tools/:id" element={<AIToolDetailPage />} />

          {/* Search */}
          <Route path="/search" element={<SearchPage />} />

          {/* Approvals - Viewable by all roles */}
          <Route
            path="/approvals"
            element={
              <ProtectedRoute
                allowedRoles={[
                  UserRole.END_USER,
                  UserRole.AI_CHAMPION,
                  UserRole.AI_COUNCIL,
                  UserRole.IT_TEAM,
                  UserRole.EXECUTIVE_SPONSOR,
                  UserRole.ADMIN,
                ]}
              >
                <ApprovalsPage />
              </ProtectedRoute>
            }
          />

          {/* Reports - Champions, Council, and Admin */}
          <Route
            path="/reports"
            element={
              <ProtectedRoute allowedRoles={[UserRole.AI_CHAMPION, UserRole.AI_COUNCIL, UserRole.ADMIN]}>
                <ReportsPage />
              </ProtectedRoute>
            }
          />

          {/* Admin - Admin only */}
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
                <AdminPage />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Error pages */}
        <Route path="/unauthorized" element={<UnauthorizedPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
